// Finding pages by name, for the top search, the search page and the app launcher. Every area home, every courageMFG
// page in an area's navigation and apps, and every Business Central page, matched as the person types.
import { SUITE, areaHref, areaNav, type Domain } from "./suite";
import { areaOpen, canOpen, type SecMe } from "./security/store";

export type PageHit = { key: string; name: string; where: string; to?: string; bc?: number; note?: string };

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

function index(access: SecMe | null): (PageHit & { words: string })[] {
  const out: (PageHit & { words: string })[] = [];
  const seen = new Set<string>();
  const add = (d: Domain, h: Omit<PageHit, "key" | "where"> & { where?: string }, extra = "") => {
    const key = h.to ? `to:${h.to}` : `bc:${h.bc}`;
    if (seen.has(key)) return;
    if (h.to && !canOpen(access, h.to.split("?")[0])) return;
    seen.add(key);
    out.push({ ...h, key, where: h.where ?? d.name, words: norm(`${h.name} ${extra}`) });
  };
  for (const d of SUITE) {
    if (!areaOpen(access, d.id)) continue;
    add(d, { name: d.name, to: areaHref(d), where: "Area home", note: d.summary }, d.id);
    for (const p of areaNav(d)) {
      if (p.name === "Home" || p.to?.startsWith("/a/")) continue;
      if (p.to || p.bc) add(d, { name: p.name, to: p.to, bc: p.to ? undefined : p.bc, note: p.note });
    }
    for (const m of d.modules) {
      if (m.status !== "live" && m.status !== "bc") continue;
      if (m.status === "live" && m.path) add(d, { name: m.name, to: m.path, note: m.summary });
      for (const p of m.pages ?? []) {
        // A page named after its app ("All accounts") or a note ("Quotes are created on…") reads better under the app.
        if (p.to && !/ are | is /.test(p.name)) add(d, { name: p.name, to: p.to, where: `${d.name} · ${m.name}` }, m.name);
        else if (p.bc && !p.to) add(d, { name: p.name, bc: p.bc, where: `${d.name} · Business Central` });
      }
    }
  }
  return out;
}

let cache: { access: SecMe | null; rows: ReturnType<typeof index> } | null = null;

/** Pages whose words start with what was typed: the whole name first, then names that start with it, then the rest. */
export function findPages(q: string, access: SecMe | null, max = 8): PageHit[] {
  const t = norm(q);
  if (!t) return [];
  if (!cache || cache.access !== access) cache = { access, rows: index(access) };
  const parts = t.split(" ");
  const scored: { h: PageHit; s: number }[] = [];
  for (const r of cache.rows) {
    // Every word typed starts a word of the page ("count" finds Counts, not Accounts).
    if (!parts.every((p) => (" " + r.words).includes(" " + p))) continue;
    const name = norm(r.name);
    const s = name === t ? 0 : name.startsWith(t) ? 1 : (" " + name).includes(" " + parts[0]) ? 2 : r.words.startsWith(t) ? 3 : 4;
    scored.push({ h: r, s: s + (r.bc ? 0.5 : 0) });
  }
  return scored.sort((a, b) => a.s - b.s || a.h.name.length - b.h.name.length).slice(0, max).map((x) => {
    const { words: _w, ...h } = x.h as PageHit & { words?: string };
    return h;
  });
}
